import { motion } from "framer-motion";
import { SERVICES } from "@/lib/constants";
import { Diamond } from "lucide-react";
import { RangoliCorner } from "@/components/landing/Decorations";

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 md:py-36 border-t border-white/5 overflow-hidden"
      style={{ background: "var(--iw-bg-2)" }}
    >
      {/* Rangoli corners */}
      <div className="absolute top-6 left-6 z-0 pointer-events-none opacity-60">
        <RangoliCorner size={140} rotate={0} />
      </div>
      <div className="absolute top-6 right-6 z-0 pointer-events-none opacity-60">
        <RangoliCorner size={140} rotate={90} />
      </div>
      <div className="absolute bottom-6 left-6 z-0 pointer-events-none opacity-60">
        <RangoliCorner size={140} rotate={270} color="rose" />
      </div>
      <div className="absolute bottom-6 right-6 z-0 pointer-events-none opacity-60">
        <RangoliCorner size={140} rotate={180} color="rose" />
      </div>
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left sticky intro */}
          <div className="lg:col-span-5 lg:sticky lg:top-28 h-fit">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-8 h-px bg-[var(--iw-gold)]" />
              <span className="overline text-[var(--iw-gold)]">What We Craft</span>
            </div>
            <h2
              className="font-serif-display text-[var(--iw-ivory)] text-5xl md:text-6xl leading-[0.95] tracking-tight"
              style={{ fontFamily: "Cormorant Garamond, serif" }}
            >
              Designed to be
              <br />
              <span className="italic gold-rose-gradient">
                remembered.
              </span>
            </h2>
            <p className="text-[var(--iw-muted)] text-base leading-relaxed mt-8 max-w-md">
              Premium digital invitations, stationery and wardrobe styling —
              crafted to captivate your guests and inspire sharing.
            </p>
          </div>

          {/* Right service list */}
          <div className="lg:col-span-7">
            <ul className="divide-y divide-white/10 border-y border-white/10">
              {SERVICES.map((s, idx) => (
                <motion.li
                  key={s.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{
                    duration: 0.8,
                    delay: idx * 0.08,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                  className="group py-8 md:py-10 flex gap-6 md:gap-10 items-start cursor-default transition-all duration-500 hover:bg-white/[0.02] hover:pl-6 md:hover:pl-8 px-2 md:px-4 relative"
                  data-testid={`service-row-${s.id}`}
                >
                  {/* Left rose accent bar that slides in */}
                  <span
                    aria-hidden="true"
                    className="absolute left-0 top-1/2 -translate-y-1/2 w-0 h-10 bg-gradient-to-b from-[var(--iw-gold)] to-[var(--iw-rose)] group-hover:w-[3px] transition-all duration-500"
                  />
                  <span
                    className="font-serif-display text-[var(--iw-gold-dim)] group-hover:text-[var(--iw-rose)] transition-colors text-3xl md:text-4xl shrink-0 w-14"
                    style={{ fontFamily: "Cormorant Garamond, serif" }}
                  >
                    {s.number}
                  </span>
                  <div className="flex-1">
                    <h3
                      className="font-serif-display text-[var(--iw-ivory)] text-3xl md:text-4xl leading-tight"
                      style={{ fontFamily: "Cormorant Garamond, serif" }}
                    >
                      {s.title}
                    </h3>
                    <p className="text-[var(--iw-muted)] mt-3 text-base leading-relaxed max-w-xl">
                      {s.desc}
                    </p>
                  </div>
                </motion.li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
